import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';
import { AudioProcessor } from './services/AudioProcessor';
import { StreamingClient } from './services/StreamingClient';
import { WebMParser } from './services/WebMParser';
import { AudioFile, PlayMode, StreamStatus } from './types';

// Auto-detect WebSocket URL from current page location
function getDefaultWebSocketUrl(): string {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    
    // Check if we are running under a subpath (e.g. /streaming/)
    const pathPrefix = window.location.pathname.includes('/streaming/') ? '/streaming' : '';
    
    return `${protocol}//${host}${pathPrefix}/api/stream/push`;
  }
  return 'ws://localhost:5122/api/stream/push';
}

function App() {
  const [files, setFiles] = useState<AudioFile[]>([]);
  const [playMode, setPlayMode] = useState<PlayMode>('list-loop');
  const [shuffle, setShuffle] = useState(false);
  const [serverUrl, setServerUrl] = useState(getDefaultWebSocketUrl());
  const [status, setStatus] = useState<StreamStatus>({
    isStreaming: false,
    progress: 0,
    totalDuration: 0,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [callInfo, setCallInfo] = useState({
    phoneNumber: '',
    direction: 'incoming' as 'incoming' | 'outgoing',
  });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEventType, setWebhookEventType] = useState<number>(1); // 1 = StreamStarted
  const [webhookIsActive, setWebhookIsActive] = useState<boolean>(true);
  const [webhookHeaders, setWebhookHeaders] = useState<Array<{key: string, value: string}>>([]);
  const [webhookTimeoutSeconds, setWebhookTimeoutSeconds] = useState<number | ''>(30);
  const [webhookRetryCount, setWebhookRetryCount] = useState<number | ''>(3);
  const [registeredWebhooks, setRegisteredWebhooks] = useState<Array<{id: string, url: string, eventType: number, isActive: boolean, headers?: Record<string, string>, timeoutSeconds?: number, retryCount?: number}>>([]);
  const [webhookServerUrl, setWebhookServerUrl] = useState(() => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      const host = window.location.host;
      // Check if we are running under a subpath (e.g. /streaming/)
      const pathPrefix = window.location.pathname.includes('/streaming/') ? '/streaming' : '';
      return `${protocol}//${host}${pathPrefix}`;
    }
    return 'http://localhost:5122';
  });

  const audioProcessorRef = useRef<AudioProcessor | null>(null);
  const streamingClientRef = useRef<StreamingClient | null>(null);
  const currentFileIndexRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const errorHandlerRef = useRef<((error: Error) => void) | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const webmParserRef = useRef<WebMParser | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(event.target.files || []);
    const audioFiles: AudioFile[] = uploadedFiles.map((file) => ({
      file,
      name: file.name,
    }));
    setFiles((prev) => [...prev, ...audioFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getNextFileIndex = (currentIndex: number): number | null => {
    if (files.length === 0) return null;

    let nextIndex: number;

    if (shuffle) {
      // Random selection
      nextIndex = Math.floor(Math.random() * files.length);
    } else {
      // Sequential
      nextIndex = (currentIndex + 1) % files.length;
    }

    // Check play mode
    if (playMode === 'list-once' && nextIndex === 0) {
      return null; // Finished list (reached the beginning again)
    }
    return nextIndex;
  };

  const getPrevFileIndex = (currentIndex: number): number | null => {
    if (files.length === 0) return null;

    let prevIndex: number;

    if (shuffle) {
      // Random selection
      prevIndex = Math.floor(Math.random() * files.length);
    } else {
      // Sequential
      prevIndex = currentIndex === 0 ? files.length - 1 : currentIndex - 1;
    }

    return prevIndex;
  };

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const playFile = async (fileIndex: number): Promise<void> => {
    if (fileIndex >= files.length || fileIndex < 0) {
      return;
    }

    const audioFile = files[fileIndex];
    currentFileIndexRef.current = fileIndex;
    setIsPaused(false);

    try {
      // Initialize audio processor
      if (!audioProcessorRef.current) {
        audioProcessorRef.current = new AudioProcessor();
        await audioProcessorRef.current.initialize();
        
        // Setup error handler
        errorHandlerRef.current = (error: Error) => {
          console.error('AudioProcessor error:', error);
          stopStreaming();
          const errorMessage = error.message || String(error);
          alert(`Audio processing error: ${errorMessage}\n\nPlease check:\n1. Audio file format is supported\n2. Browser console for details`);
        };
        audioProcessorRef.current.setOnError(errorHandlerRef.current);
      }

      // Initialize streaming client (reuse connection if exists)
      if (!streamingClientRef.current) {
        streamingClientRef.current = new StreamingClient(serverUrl);
        try {
          await streamingClientRef.current.connect();
        } catch (error) {
          throw new Error(`Failed to connect to server at ${serverUrl}: ${error instanceof Error ? error.message : String(error)}\n\nPlease ensure the server is running.`);
        }

        streamingClientRef.current.setOnStreamId((streamId) => {
          setStatus((prev) => ({ ...prev, streamId }));
        });
      }
      // Reuse existing connection - no need to send STREAM_END when switching files

      // Reset WebM parser for new file
      if (!webmParserRef.current) {
        webmParserRef.current = new WebMParser();
      } else {
        webmParserRef.current.reset();
      }

      // Process audio file
      let duration: number;
      try {
        duration = await audioProcessorRef.current.processAudioFile(
          audioFile.file,
          async (opusBlob: Blob) => {
            try {
              if (!isPaused && isPlayingRef.current) {
                const arrayBuffer = await opusBlob.arrayBuffer();
                const audioData = new Uint8Array(arrayBuffer);
                const parser = webmParserRef.current;
                if (streamingClientRef.current) {
                  if (parser) {
                    const frames = parser.addChunk(audioData);
                    if (frames.length > 0) {
                      frames.forEach((frame) => {
                        streamingClientRef.current?.sendAudioData(frame);
                      });
                    }
                  } else {
                    streamingClientRef.current.sendAudioData(audioData);
                  }
                }
              }
            } catch (error) {
              console.error('Error processing Opus data:', error);
            }
          }
        );
      } catch (error) {
        throw new Error(`Failed to process audio file "${audioFile.name}": ${error instanceof Error ? error.message : String(error)}`);
      }

      setStatus((prev) => ({
        ...prev,
        currentFile: audioFile.name,
        totalDuration: duration,
        progress: 0,
      }));

      // Send stream start only if this is a new stream (first file or after reconnection)
      if (streamingClientRef.current && !streamingClientRef.current.getStreamId()) {
        const metadata: any = {
          sampleRate: 16000,
          channels: 1,
          bitDepth: 16,
          codec: 'opus',
          bitrate: 48000,
        };

        // Add CallInfo if phone number is provided or direction is not default
        if (callInfo.phoneNumber.trim() || callInfo.direction !== 'incoming') {
          metadata.callInfo = {
            phoneNumber: callInfo.phoneNumber.trim() || undefined,
            direction: callInfo.direction,
            timestamp: new Date().toISOString(),
          };
        }

        streamingClientRef.current.sendStreamStart(metadata);
      }
      // If stream already exists, just continue sending audio data without sending STREAM_START again

      // Start recording
      try {
        audioProcessorRef.current.startRecording();
      } catch (error) {
        throw new Error(`Failed to start recording: ${error instanceof Error ? error.message : String(error)}`);
      }

      // Update progress
      startTimeRef.current = Date.now() - pausedTimeRef.current * 1000;
      pausedTimeRef.current = 0;
      clearProgressInterval();
      
      progressIntervalRef.current = setInterval(() => {
        if (!isPaused && isPlayingRef.current) {
          const elapsed = (Date.now() - startTimeRef.current) / 1000;
          setStatus((prev) => ({
            ...prev,
            progress: Math.min(elapsed, duration),
          }));

          if (elapsed >= duration) {
            clearProgressInterval();
            audioProcessorRef.current?.stopRecording();

            // Check if should play next file
            const nextIndex = getNextFileIndex(fileIndex);
            if (nextIndex !== null) {
              // Switch to next file - reuse same connection and stream
              setTimeout(() => playFile(nextIndex), 100);
            } else {
              stopStreaming();
            }
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error playing file:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : error instanceof Event
        ? `Event error: ${error.type}`
        : String(error);
      alert(`Error playing file: ${errorMessage}\n\nPlease check:\n1. Audio file format is supported\n2. Server is running at ${serverUrl}\n3. Browser console for details`);
      stopStreaming();
    }
  };

  const togglePlayPause = () => {
    if (!status.isStreaming) {
      startStreaming();
    } else if (isPaused) {
      // Resume
      setIsPaused(false);
      startTimeRef.current = Date.now() - pausedTimeRef.current * 1000;
      if (audioProcessorRef.current) {
        audioProcessorRef.current.startRecording();
      }
    } else {
      // Pause
      setIsPaused(true);
      pausedTimeRef.current = status.progress;
      clearProgressInterval();
      if (audioProcessorRef.current) {
        audioProcessorRef.current.stopRecording();
      }
    }
  };

  const handleNext = () => {
    if (files.length === 0) return;
    
    if (!status.isStreaming) {
      // Not playing - start playing next file
      let startIndex = 0;
      if (shuffle) {
        startIndex = Math.floor(Math.random() * files.length);
      } else {
        startIndex = currentFileIndexRef.current < files.length - 1 
          ? currentFileIndexRef.current + 1 
          : 0;
      }
      startStreamingFromIndex(startIndex);
      return;
    }
    
    // Already playing - switch to next file
    const nextIndex = getNextFileIndex(currentFileIndexRef.current);
    if (nextIndex !== null) {
      clearProgressInterval();
      audioProcessorRef.current?.stopRecording();
      // Switch to next file - reuse same connection and stream
      setTimeout(() => playFile(nextIndex), 100);
    }
  };

  const handlePrev = () => {
    if (!status.isStreaming || files.length === 0) return;
    const prevIndex = getPrevFileIndex(currentFileIndexRef.current);
    if (prevIndex !== null) {
      clearProgressInterval();
      audioProcessorRef.current?.stopRecording();
      // Switch to previous file - reuse same connection and stream
      setTimeout(() => playFile(prevIndex), 100);
    }
  };

  const startStreamingFromIndex = async (startIndex: number = 0) => {
    if (files.length === 0) {
      alert('Please upload at least one audio file');
      return;
    }

    if (isPlayingRef.current) {
      return;
    }

    isPlayingRef.current = true;
    setStatus((prev) => ({ ...prev, isStreaming: true }));

    await playFile(startIndex);
  };

  const startStreaming = async () => {
    // Determine starting file
    let startIndex = 0;
    if (shuffle) {
      startIndex = Math.floor(Math.random() * files.length);
    }
    await startStreamingFromIndex(startIndex);
  };

  const stopStreaming = () => {
    isPlayingRef.current = false;
    setIsPaused(false);
    clearProgressInterval();
    audioProcessorRef.current?.cleanup();
    audioProcessorRef.current = null;
    streamingClientRef.current?.disconnect();
    streamingClientRef.current = null;
    pausedTimeRef.current = 0;
    startTimeRef.current = 0;
    webmParserRef.current?.reset();

    setStatus({
      isStreaming: false,
      progress: 0,
      totalDuration: 0,
    });
  };

  useEffect(() => {
    return () => {
      clearProgressInterval();
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addWebhookHeader = () => {
    setWebhookHeaders([...webhookHeaders, { key: '', value: '' }]);
  };

  const removeWebhookHeader = (index: number) => {
    setWebhookHeaders(webhookHeaders.filter((_, i) => i !== index));
  };

  const updateWebhookHeader = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...webhookHeaders];
    updated[index] = { ...updated[index], [field]: value };
    setWebhookHeaders(updated);
  };

  const registerWebhook = async () => {
    if (!webhookUrl.trim()) {
      alert('Please enter a webhook URL');
      return;
    }

    try {
      // Build headers object from array
      const headersObj: Record<string, string> = {};
      webhookHeaders.forEach(header => {
        if (header.key.trim() && header.value.trim()) {
          headersObj[header.key.trim()] = header.value.trim();
        }
      });

      const requestBody: any = {
        url: webhookUrl.trim(),
        eventType: webhookEventType,
        isActive: webhookIsActive,
      };

      // Only include optional fields if they have values
      if (Object.keys(headersObj).length > 0) {
        requestBody.headers = headersObj;
      }
      if (webhookTimeoutSeconds !== '') {
        requestBody.timeoutSeconds = webhookTimeoutSeconds;
      }
      if (webhookRetryCount !== '') {
        requestBody.retryCount = webhookRetryCount;
      }

      const response = await fetch(`${webhookServerUrl}/api/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to register webhook');
      }

      const webhook = await response.json();
      setRegisteredWebhooks([...registeredWebhooks, webhook]);
      
      // Reset form
      setWebhookUrl('');
      setWebhookHeaders([]);
      setWebhookTimeoutSeconds(30);
      setWebhookRetryCount(3);
      setWebhookIsActive(true);
      
      alert(`Webhook registered successfully! ID: ${webhook.id}`);
    } catch (error) {
      console.error('Error registering webhook:', error);
      alert(`Failed to register webhook: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const deleteWebhook = async (webhookId: string) => {
    try {
      const response = await fetch(`${webhookServerUrl}/api/webhooks/${webhookId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete webhook');
      }

      setRegisteredWebhooks(registeredWebhooks.filter(w => w.id !== webhookId));
      alert('Webhook deleted successfully');
    } catch (error) {
      console.error('Error deleting webhook:', error);
      alert(`Failed to delete webhook: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const loadWebhooks = useCallback(async () => {
    try {
      const response = await fetch(`${webhookServerUrl}/api/webhooks`);
      if (response.ok) {
        const webhooks = await response.json();
        setRegisteredWebhooks(webhooks);
      }
    } catch (error) {
      console.error('Error loading webhooks:', error);
    }
  }, [webhookServerUrl]);

  useEffect(() => {
    loadWebhooks();
  }, [loadWebhooks]);

  return (
    <div className="App">
      <div className="main-container">
      <div className="player-container">
        {/* File List Section */}
        <div className="file-list-section">
          <div className="file-list-header">
            <h2>Playlist</h2>
            <button
              className="add-files-btn"
              onClick={handleFileButtonClick}
              disabled={status.isStreaming}
            >
              + Add Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              onChange={handleFileUpload}
              disabled={status.isStreaming}
              style={{ display: 'none' }}
            />
          </div>
          {files.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🎵</div>
              <div className="empty-state-text">No files added</div>
            </div>
          ) : (
            <div className="file-list">
              {files.map((file, index) => (
                <div
                  key={index}
                  className={`file-item ${
                    status.isStreaming && currentFileIndexRef.current === index
                      ? 'active'
                      : ''
                  }`}
                >
                  <span className="file-index">{index + 1}</span>
                  <span className="file-name" title={file.name}>
                    {file.name}
                  </span>
                  <button
                    className="file-remove-btn"
                    onClick={() => removeFile(index)}
                    disabled={status.isStreaming}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Player Controls Section */}
        <div className="player-controls-section">
          {/* Current Track Info */}
          {status.currentFile && (
            <div className="current-track">
              <div className="track-name">{status.currentFile}</div>
              {status.streamId && (
                <div className="stream-id">{status.streamId}</div>
              )}
            </div>
          )}

          {/* Progress Bar */}
          {status.isStreaming && status.totalDuration > 0 && (
            <div className="progress-section">
              <div className="progress-time">
                <span>{formatTime(status.progress)}</span>
                <span>{formatTime(status.totalDuration)}</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(status.progress / status.totalDuration) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="control-buttons">
            <button
              className={`control-btn shuffle-btn ${shuffle ? 'active' : ''}`}
              onClick={() => setShuffle(!shuffle)}
              disabled={files.length === 0}
              title="Shuffle"
            >
              🔀
            </button>
            <button
              className="control-btn prev-btn"
              onClick={handlePrev}
              disabled={files.length === 0 || !status.isStreaming}
              title="Previous"
            >
              ⏮
            </button>
            <button
              className="control-btn play-pause-btn"
              onClick={togglePlayPause}
              disabled={files.length === 0}
              title={status.isStreaming ? (isPaused ? 'Play' : 'Pause') : 'Play'}
            >
              {status.isStreaming && !isPaused ? '⏸' : '▶'}
            </button>
            <button
              className="control-btn stop-btn"
              onClick={stopStreaming}
              disabled={!status.isStreaming}
              title="Stop"
            >
              ⏹
            </button>
            <button
              className="control-btn next-btn"
              onClick={handleNext}
              disabled={files.length === 0}
              title="Next"
            >
              ⏭
            </button>
            <button
              className={`control-btn repeat-btn ${playMode === 'list-loop' ? 'active' : ''}`}
              onClick={() => {
                setPlayMode(playMode === 'list-loop' ? 'list-once' : 'list-loop');
              }}
              disabled={files.length === 0}
              title={playMode === 'list-loop' ? 'Loop: On' : 'Loop: Off'}
            >
              {playMode === 'list-loop' ? '🔁' : '➡️'}
            </button>
          </div>

          {/* Settings */}
          <div className="player-settings">
            <div className="setting-group">
              <label>Server URL:</label>
              <input
                type="text"
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                disabled={status.isStreaming}
                className="server-input"
              />
            </div>
            <div className="setting-group">
              <label>Play Mode:</label>
              <select
                value={playMode}
                onChange={(e) => setPlayMode(e.target.value as PlayMode)}
                disabled={status.isStreaming}
                className="mode-select"
              >
                <option value="list-once">List - Once</option>
                <option value="list-loop">List - Loop</option>
              </select>
            </div>
            <div className="setting-group">
              <label>Call Information:</label>
              <div className="call-info-inputs">
                <input
                  type="text"
                  placeholder="Phone Number (optional)"
                  value={callInfo.phoneNumber}
                  onChange={(e) => setCallInfo({ ...callInfo, phoneNumber: e.target.value })}
                  disabled={status.isStreaming}
                  className="phone-input"
                />
                <select
                  value={callInfo.direction}
                  onChange={(e) => setCallInfo({ ...callInfo, direction: e.target.value as 'incoming' | 'outgoing' })}
                  disabled={status.isStreaming}
                  className="direction-select"
                >
                  <option value="incoming">Incoming</option>
                  <option value="outgoing">Outgoing</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Webhook Registration Section */}
        <div className="webhook-container">
          <div className="webhook-header">
            <h2>Webhook Registration</h2>
            <button
              className="refresh-btn"
              onClick={loadWebhooks}
              title="Refresh webhooks"
            >
              🔄
            </button>
          </div>

          <div className="webhook-form">
            <div className="webhook-form-group">
              <label>Server URL:</label>
              <input
                type="text"
                value={webhookServerUrl}
                onChange={(e) => setWebhookServerUrl(e.target.value)}
                className="webhook-server-input"
                placeholder="http://localhost:5122"
              />
            </div>
            <div className="webhook-form-group">
              <label>Webhook URL: <span className="required">*</span></label>
              <input
                type="text"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="webhook-url-input"
                placeholder="https://your-server.com/webhook"
              />
            </div>
            <div className="webhook-form-group">
              <label>Event Type:</label>
              <select
                value={webhookEventType}
                onChange={(e) => setWebhookEventType(Number(e.target.value))}
                className="webhook-event-select"
              >
                <option value={1}>Stream Started</option>
                <option value={2}>Stream Ended</option>
                <option value={3}>Subscriber Connected</option>
                <option value={4}>Subscriber Disconnected</option>
              </select>
            </div>
            <div className="webhook-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={webhookIsActive}
                  onChange={(e) => setWebhookIsActive(e.target.checked)}
                  className="webhook-checkbox"
                />
                <span>Active</span>
              </label>
            </div>
            <div className="webhook-form-group">
              <label>Headers:</label>
              <div className="webhook-headers-list">
                {webhookHeaders.map((header, index) => (
                  <div key={index} className="webhook-header-item">
                    <input
                      type="text"
                      placeholder="Header name"
                      value={header.key}
                      onChange={(e) => updateWebhookHeader(index, 'key', e.target.value)}
                      className="webhook-header-key"
                    />
                    <span className="webhook-header-separator">:</span>
                    <input
                      type="text"
                      placeholder="Header value"
                      value={header.value}
                      onChange={(e) => updateWebhookHeader(index, 'value', e.target.value)}
                      className="webhook-header-value"
                    />
                    <button
                      type="button"
                      className="webhook-header-remove-btn"
                      onClick={() => removeWebhookHeader(index)}
                      title="Remove header"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="webhook-header-add-btn"
                  onClick={addWebhookHeader}
                >
                  + Add Header
                </button>
              </div>
            </div>
            <div className="webhook-form-group">
              <label>Timeout (seconds):</label>
              <input
                type="number"
                value={webhookTimeoutSeconds}
                onChange={(e) => setWebhookTimeoutSeconds(e.target.value === '' ? '' : Number(e.target.value))}
                className="webhook-number-input"
                placeholder="30"
                min="1"
              />
            </div>
            <div className="webhook-form-group">
              <label>Retry Count:</label>
              <input
                type="number"
                value={webhookRetryCount}
                onChange={(e) => setWebhookRetryCount(e.target.value === '' ? '' : Number(e.target.value))}
                className="webhook-number-input"
                placeholder="3"
                min="0"
              />
            </div>
            <button
              className="register-webhook-btn"
              onClick={registerWebhook}
              disabled={!webhookUrl.trim()}
            >
              Register Webhook
            </button>
          </div>

          <div className="webhook-list-section">
            <h3>Registered Webhooks ({registeredWebhooks.length})</h3>
            {registeredWebhooks.length === 0 ? (
              <div className="webhook-empty-state">
                <div className="webhook-empty-icon">🔔</div>
                <div className="webhook-empty-text">No webhooks registered</div>
              </div>
            ) : (
              <div className="webhook-list">
                {registeredWebhooks.map((webhook) => (
                  <div key={webhook.id} className="webhook-item">
                    <div className="webhook-info">
                      <div className="webhook-url" title={webhook.url}>
                        {webhook.url}
                      </div>
                      <div className="webhook-meta">
                        <span className="webhook-event-badge">
                          {webhook.eventType === 1 && 'Stream Started'}
                          {webhook.eventType === 2 && 'Stream Ended'}
                          {webhook.eventType === 3 && 'Subscriber Connected'}
                          {webhook.eventType === 4 && 'Subscriber Disconnected'}
                        </span>
                        {!webhook.isActive && (
                          <span className="webhook-inactive-badge">Inactive</span>
                        )}
                        <span className="webhook-id">ID: {webhook.id.substring(0, 8)}...</span>
                      </div>
                    </div>
                    <button
                      className="webhook-delete-btn"
                      onClick={() => deleteWebhook(webhook.id)}
                      title="Delete webhook"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
